/* 168VELO — theme.js
   Small, dependency-free custom elements. Everything degrades to plain
   forms and links when JS is unavailable. */
(function () {
  'use strict';

  var theme = window.theme || {};
  var routes = theme.routes || {};
  var strings = theme.strings || {};

  /* ---------------------------------------------------------------- utils */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var fmt = format || theme.moneyFormat || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(number) || number == null) return 0;
      var parts = (number / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var match = fmt.match(placeholder);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  function toast(message, type) {
    var el = document.getElementById('Toast');
    if (!el || !message) return;
    el.textContent = message;
    el.className = 'toast is-visible' + (type === 'error' ? ' toast--error' : '');
    clearTimeout(el._timer);
    el._timer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, 3600);
  }

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  function trapFocus(container) {
    function onKey(e) {
      if (e.key !== 'Tab') return;
      var items = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', onKey);
    return function () {
      container.removeEventListener('keydown', onKey);
    };
  }

  /* ------------------------------------------------------------ overlays */
  var openLayers = [];

  function syncScrollLock() {
    document.body.classList.toggle('is-locked', openLayers.length > 0);
  }

  function openDrawer(el, opener) {
    if (!el || el.classList.contains('is-open')) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    el._opener = opener || document.activeElement;
    if (opener && opener.setAttribute) opener.setAttribute('aria-expanded', 'true');
    var panel = el.querySelector('.drawer__panel') || el;
    el._untrap = trapFocus(panel);
    openLayers.push(el);
    syncScrollLock();
    requestAnimationFrame(function () {
      panel.focus({ preventScroll: true });
    });
    el.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  function closeDrawer(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (el._untrap) el._untrap();
    var opener = el._opener;
    if (opener && opener.setAttribute) opener.setAttribute('aria-expanded', 'false');
    openLayers = openLayers.filter(function (l) {
      return l !== el;
    });
    syncScrollLock();
    if (opener && document.contains(opener) && opener.focus) opener.focus({ preventScroll: true });
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-drawer-open'));
      if (target) {
        e.preventDefault();
        openDrawer(target, opener);
      }
      return;
    }
    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      var drawer = closer.closest('.drawer');
      if (drawer) {
        if (closer.tagName === 'A') return; // links inside drawers navigate normally
        e.preventDefault();
        closeDrawer(drawer);
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !openLayers.length) return;
    var top = openLayers[openLayers.length - 1];
    if (top.tagName === 'SEARCH-MODAL') top.close();
    else closeDrawer(top);
  });

  /* ------------------------------------------------------- sticky header */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      var self = this;
      var set = function () {
        var wrapper = self.closest('.shopify-section') || self;
        document.documentElement.style.setProperty('--header-offset', wrapper.offsetHeight + 'px');
      };
      set();
      if ('ResizeObserver' in window) new ResizeObserver(set).observe(this);
      var onScroll = function () {
        self.classList.toggle('is-scrolled', window.scrollY > 8);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* Header dropdowns: hover on mouse devices, click/keyboard everywhere. */
  var hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  function initDropdowns(root) {
    (root || document).querySelectorAll('details[data-dropdown]').forEach(function (d) {
      if (d._bound) return;
      d._bound = true;
      var timer;
      var summary = d.querySelector('summary');
      d.addEventListener('mouseenter', function () {
        if (!hoverQuery.matches) return;
        clearTimeout(timer);
        d.setAttribute('open', '');
      });
      d.addEventListener('mouseleave', function () {
        if (!hoverQuery.matches) return;
        timer = setTimeout(function () {
          d.removeAttribute('open');
        }, 160);
      });
      summary.addEventListener('click', function (e) {
        if (hoverQuery.matches && e.detail > 0 && d.hasAttribute('open')) e.preventDefault();
      });
      d.addEventListener('toggle', function () {
        if (!d.open) return;
        document.querySelectorAll('details[data-dropdown][open]').forEach(function (o) {
          if (o !== d) o.removeAttribute('open');
        });
      });
    });
  }
  document.addEventListener('click', function (e) {
    document.querySelectorAll('details[data-dropdown][open]').forEach(function (d) {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var d = document.querySelector('details[data-dropdown][open]');
    if (d) {
      d.removeAttribute('open');
      d.querySelector('summary').focus();
    }
  });

  /* --------------------------------------------------------------- drawers */
  class MenuDrawer extends HTMLElement {}
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.setAttribute('data-count', count);
      el.textContent = count > 0 ? count : '';
    });
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      var self = this;
      document.addEventListener('click', function (e) {
        var link = e.target.closest('[data-cart-open]');
        if (!link || theme.cartType !== 'drawer') return;
        if (document.body.classList.contains('template-cart')) return;
        e.preventDefault();
        self.open(link);
      });
    }
    open(opener) {
      openDrawer(this, opener);
    }
    close() {
      closeDrawer(this);
    }
    renderSections(sections) {
      if (!sections || !sections['cart-drawer']) return this.refresh();
      var doc = parseHTML(sections['cart-drawer']);
      var next = doc.querySelector('#CartDrawerContent');
      var current = this.querySelector('#CartDrawerContent');
      if (next && current) current.innerHTML = next.innerHTML;
      var count = doc.querySelector('[data-cart-drawer-count]');
      if (count) updateCartCount(parseInt(count.textContent, 10) || 0);
    }
    refresh() {
      var self = this;
      return fetch(window.location.pathname + '?sections=cart-drawer')
        .then(function (r) {
          return r.json();
        })
        .then(function (sections) {
          self.renderSections(sections);
        });
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* ------------------------------------------------------------- quantity */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      var self = this;
      var minus = this.querySelector('[data-qty-minus]');
      var plus = this.querySelector('[data-qty-plus]');
      if (minus) minus.addEventListener('click', function () { self.step(-1); });
      if (plus) plus.addEventListener('click', function () { self.step(1); });
    }
    step(direction) {
      var input = this.input;
      var step = Number(input.step) || 1;
      var min = input.min !== '' ? Number(input.min) : 0;
      var max = input.max !== '' && input.max != null ? Number(input.max) : Infinity;
      var current = Number(input.value) || 0;
      var next = Math.min(max, Math.max(min, current + direction * step));
      if (next === current) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------------------------------------------------------- product form */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.form.querySelector('[type="submit"]');
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
    showError(message) {
      if (this.error) {
        this.error.textContent = message;
        this.error.hidden = false;
      } else {
        toast(message, 'error');
      }
    }
    onSubmit(e) {
      var drawer = document.querySelector('cart-drawer');
      if (theme.cartType !== 'drawer' || !drawer) return; // regular POST to /cart/add
      e.preventDefault();
      if (!this.button || this.button.disabled) return;
      var self = this;
      if (this.error) this.error.hidden = true;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');

      var body = new FormData(this.form);
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);

      fetch(routes.cartAdd, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: body
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          if (data.status) {
            self.showError(data.description || data.message || strings.cartError);
            return;
          }
          drawer.renderSections(data.sections);
          drawer.open(self.button);
          document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
        })
        .catch(function () {
          self.showError(strings.cartError);
        })
        .finally(function () {
          self.button.classList.remove('is-loading');
          self.button.removeAttribute('aria-busy');
        });
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------ cart items */
  class CartItems extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.addEventListener(
        'change',
        debounce(function (e) {
          var input = e.target.closest('[data-line-qty]');
          if (input) self.update(input.getAttribute('data-line-qty'), input.value, input);
        }, 350)
      );
      this.addEventListener('click', function (e) {
        var remove = e.target.closest('[data-line-remove]');
        if (!remove) return;
        e.preventDefault();
        self.update(remove.getAttribute('data-line-remove'), 0);
      });
    }
    update(line, quantity, input) {
      var lineEl = this.querySelector('[data-line="' + line + '"]');
      if (lineEl) lineEl.style.opacity = '0.45';
      var sections = ['cart-drawer'];
      var page = document.querySelector('cart-items[data-section]');
      if (page) sections.push(page.getAttribute('data-section'));

      fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
          sections: sections,
          sections_url: window.location.pathname
        })
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          if (data.status || data.errors) {
            var message = data.description || data.errors || strings.cartError;
            if (lineEl) {
              lineEl.style.opacity = '';
              var err = lineEl.querySelector('[data-line-error]');
              if (err) {
                err.textContent = message;
                err.hidden = false;
              }
            } else {
              toast(message, 'error');
            }
            if (input && input.defaultValue) input.value = input.defaultValue;
            return;
          }
          renderCart(data, line);
        })
        .catch(function () {
          if (lineEl) lineEl.style.opacity = '';
          toast(strings.cartError, 'error');
        });
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  function renderCart(cart, focusLine) {
    var active = document.activeElement;
    var activeInDrawer = active && active.closest && active.closest('cart-drawer');
    updateCartCount(cart.item_count);
    var sections = cart.sections || {};
    var drawer = document.querySelector('cart-drawer');
    if (drawer && sections['cart-drawer']) drawer.renderSections(sections);
    Object.keys(sections).forEach(function (id) {
      if (id === 'cart-drawer') return;
      var doc = parseHTML(sections[id]);
      var next = doc.querySelector('#MainCart-' + id);
      var current = document.getElementById('MainCart-' + id);
      if (next && current) current.innerHTML = next.innerHTML;
    });
    // put focus back somewhere sensible
    var scope = activeInDrawer ? drawer : document;
    var target =
      scope.querySelector('[data-line-qty="' + focusLine + '"]') ||
      scope.querySelector('[data-line-qty]') ||
      (activeInDrawer ? drawer.querySelector('.drawer__panel') : null);
    if (target && target.focus) target.focus({ preventScroll: true });
  }

  /* -------------------------------------------------------- variant picker */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
    }
    selected() {
      return Array.prototype.map.call(this.querySelectorAll('fieldset.option'), function (fs) {
        var select = fs.querySelector('select');
        if (select) return select.value;
        var checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    onChange() {
      var selected = this.selected();
      this.querySelectorAll('fieldset.option').forEach(function (fs, i) {
        var label = fs.querySelector('[data-option-selected]');
        if (label) label.textContent = selected[i];
      });
      var variant =
        this.variants.find(function (v) {
          return v.options.every(function (o, i) {
            return o === selected[i];
          });
        }) || null;
      this.updateAvailability(selected);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant } }));
    }
    updateAvailability(selected) {
      selected = selected || this.selected();
      var variants = this.variants;
      this.querySelectorAll('fieldset.option').forEach(function (fs, i) {
        fs.querySelectorAll('input[type="radio"], option').forEach(function (el) {
          var value = el.value;
          var available = variants.some(function (v) {
            return (
              v.available &&
              v.options[i] === value &&
              v.options.every(function (o, j) {
                return j === i || o === selected[j];
              })
            );
          });
          if (el.tagName === 'INPUT') {
            el.classList.toggle('is-unavailable', !available);
          } else {
            el.textContent = value + (available ? '' : ' — ' + strings.unavailable);
          }
        });
      });
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------------------------------------------------------- product info */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      var inv = this.querySelector('[data-inventory]');
      this.inventory = inv ? JSON.parse(inv.textContent) : null;
      this.addEventListener('variant:change', this.onVariantChange.bind(this));
    }
    onVariantChange(e) {
      var v = e.detail.variant;
      var q = this.querySelector.bind(this);
      var idInput = q('[data-variant-id]');
      var addBtn = q('[data-add-button]');
      var addLabel = q('[data-add-label]');
      var dynamic = q('[data-dynamic-checkout]');
      var notify = q('[data-notify]');
      var soldBadge = q('[data-soldout-badge]');
      var saleBadge = q('[data-sale-badge]');

      if (!v) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = strings.unavailable;
        if (dynamic) dynamic.hidden = true;
        return;
      }

      if (idInput) idInput.value = v.id;

      var price = q('[data-price]');
      if (price) {
        var onSale = v.compare_at_price && v.compare_at_price > v.price;
        price.classList.toggle('price--on-sale', !!onSale);
        var cur = price.querySelector('[data-price-current]');
        if (cur) cur.textContent = formatMoney(v.price);
        var wrap = price.querySelector('[data-price-compare-wrap]');
        var cmp = price.querySelector('[data-price-compare]');
        if (wrap) wrap.hidden = !onSale;
        if (cmp && onSale) cmp.textContent = formatMoney(v.compare_at_price);
        if (saleBadge) saleBadge.hidden = !onSale;
      }
      if (soldBadge) soldBadge.hidden = v.available;

      var sku = q('[data-sku]');
      if (sku) {
        sku.hidden = !v.sku;
        var skuValue = sku.querySelector('[data-sku-value]');
        if (skuValue) skuValue.textContent = v.sku || '';
      }

      if (addBtn) addBtn.disabled = !v.available;
      if (addLabel) addLabel.textContent = v.available ? strings.addToCart : strings.soldOut;
      if (dynamic) dynamic.hidden = !v.available;
      if (notify) {
        notify.hidden = v.available;
        var field = notify.querySelector('[data-notify-product]');
        if (field) {
          var base = field.value.split(' / ')[0];
          field.value = base + ' / ' + v.title + (v.sku ? ' / SKU ' + v.sku : '');
        }
      }

      var stock = q('[data-stock]');
      if (stock && this.inventory) {
        var info = this.inventory[v.id] || {};
        var threshold = Number(stock.getAttribute('data-threshold')) || 5;
        stock.classList.remove('is-low', 'is-out');
        if (!v.available) {
          stock.classList.add('is-out');
          stock.textContent = strings.soldOut;
        } else if (info.m === 'shopify' && info.q > 0 && info.q <= threshold) {
          stock.classList.add('is-low');
          stock.textContent = (strings.lowStock || 'Only __QTY__ left').replace('__QTY__', info.q);
        } else {
          stock.textContent = strings.inStock || 'In stock';
        }
      }

      var qty = q('quantity-input input[name="quantity"]');
      if (qty && v.quantity_rule) {
        qty.min = v.quantity_rule.min || 1;
        qty.step = v.quantity_rule.increment || 1;
        if (v.quantity_rule.max) qty.max = v.quantity_rule.max;
        else qty.removeAttribute('max');
        if (Number(qty.value) < Number(qty.min)) qty.value = qty.min;
      }

      if (this.dataset.url && window.history.replaceState) {
        window.history.replaceState({}, '', this.dataset.url + '?variant=' + v.id);
      }

      if (v.featured_media) {
        var gallery = q('product-gallery');
        if (gallery && gallery.goTo) gallery.goTo(v.featured_media.id);
      }
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* --------------------------------------------------------------- gallery */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.track = this.querySelector('[data-slides]');
      if (!this.track) return;
      this.slides = Array.prototype.slice.call(this.track.children);
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-thumb]'));
      this.counter = this.querySelector('[data-counter]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.index = 0;
      this.thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          self.goTo(t.getAttribute('data-thumb'));
        });
      });
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.show(self.index - 1); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.show(self.index + 1); });
      this.track.addEventListener(
        'scroll',
        debounce(function () {
          var i = Math.round(self.track.scrollLeft / Math.max(1, self.track.clientWidth));
          if (i !== self.index) {
            self.index = i;
            self.update();
          }
        }, 60),
        { passive: true }
      );
      this.track.setAttribute('tabindex', '0');
      this.track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); self.show(self.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.show(self.index - 1); }
      });
      var initial = this.getAttribute('data-initial');
      if (initial) this.goTo(initial, false);
      this.update();
    }
    show(i, smooth) {
      if (!this.slides.length) return;
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      this.index = i;
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth === false ? 'auto' : 'smooth' });
      this.update();
    }
    goTo(mediaId, smooth) {
      var i = this.slides.findIndex(function (s) {
        return String(s.getAttribute('data-media-id')) === String(mediaId);
      });
      if (i >= 0) this.show(i, smooth);
    }
    update() {
      var i = this.index;
      var total = this.slides.length;
      if (this.counter) this.counter.textContent = pad(i + 1) + ' / ' + pad(total);
      this.thumbs.forEach(function (t, n) {
        if (n === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (this.prevBtn) this.prevBtn.disabled = i <= 0;
      if (this.nextBtn) this.nextBtn.disabled = i >= total - 1;
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ---------------------------------------------------------- search modal */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('#PredictiveSearchResults');
      this.predictive = this.getAttribute('data-predictive') === 'true' && routes.predictiveSearch;
      document.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-search-open]');
        if (!opener) return;
        e.preventDefault();
        self.open(opener);
      });
      var closeBtn = this.querySelector('[data-search-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });
      this.addEventListener('click', function (e) {
        if (e.target === self || e.target === self.results) self.close();
      });
      if (this.predictive && this.input) {
        this.input.addEventListener('input', debounce(function () { self.search(); }, 250));
      }
    }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener;
      this.classList.add('is-open');
      this._untrap = trapFocus(this);
      openLayers.push(this);
      syncScrollLock();
      var input = this.input;
      setTimeout(function () {
        if (input) {
          input.focus();
          input.select();
        }
      }, 40);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      if (this._untrap) this._untrap();
      var self = this;
      openLayers = openLayers.filter(function (l) {
        return l !== self;
      });
      syncScrollLock();
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
    search() {
      var self = this;
      var term = this.input.value.trim();
      if (this.controller) this.controller.abort();
      if (!term) {
        this.results.innerHTML = '';
        return;
      }
      this.controller = 'AbortController' in window ? new AbortController() : null;
      var url =
        routes.predictiveSearch +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,collection,page,article,query' +
        '&resources[limit]=6&resources[limit_scope]=each' +
        '&section_id=predictive-search';
      fetch(url, this.controller ? { signal: this.controller.signal } : {})
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          var doc = parseHTML(html);
          var inner = doc.querySelector('[data-predictive-results]');
          self.results.innerHTML = inner ? inner.outerHTML : '';
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
        });
    }
  }
  if (!customElements.get('search-modal')) customElements.define('search-modal', SearchModal);

  /* ---------------------------------------------------------------- facets */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.sectionId = this.getAttribute('data-section');
      var debounced = debounce(function (form) {
        self.fromForm(form);
      }, 500);
      this.addEventListener('change', function (e) {
        var form = e.target.closest('form[data-facets-form]');
        if (!form) return;
        if (e.target.type === 'number') debounced(form);
        else self.fromForm(form);
      });
      this.addEventListener('input', function (e) {
        if (e.target.type !== 'number') return;
        var form = e.target.closest('form[data-facets-form]');
        if (form) debounced(form);
      });
      this.addEventListener('submit', function (e) {
        var form = e.target.closest('form[data-facets-form]');
        if (!form) return;
        e.preventDefault();
        self.fromForm(form);
      });
      this.addEventListener('click', function (e) {
        var link = e.target.closest('a[data-facet-link]');
        if (!link) return;
        e.preventDefault();
        var drawer = link.closest('.drawer');
        self.render(link.href, true, !drawer);
      });
      document.addEventListener('click', function (e) {
        self.querySelectorAll('.facets__bar details[open]').forEach(function (d) {
          if (!d.contains(e.target)) d.removeAttribute('open');
        });
      });
      window.addEventListener('popstate', function (e) {
        if (e.state && e.state.facets) self.render(window.location.href, false);
      });
    }
    fromForm(form) {
      var params = new URLSearchParams();
      new FormData(form).forEach(function (value, key) {
        if (value !== '') params.append(key, value);
      });
      if (!form.hasAttribute('data-sort-form')) {
        var sort = this.querySelector('[data-sort-form] select');
        if (sort) params.set('sort_by', sort.value);
      }
      params.delete('page');
      this.render(window.location.pathname + '?' + params.toString(), true);
    }
    render(url, push, scroll) {
      var self = this;
      var results = this.querySelector('[data-results]');
      if (results) results.classList.add('is-loading');
      var openIds = Array.prototype.map
        .call(this.querySelectorAll('.facets-drawer details[open]'), function (d) {
          return d.id;
        })
        .filter(Boolean);
      var focusedId = document.activeElement && document.activeElement.id;
      var fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      fetch(fetchUrl.toString())
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          var doc = parseHTML(html);
          [['[data-facets-content]'], ['[data-drawer-facets]'], ['[data-count]'], ['[data-count-num]']].forEach(function (pair) {
            var next = doc.querySelector(pair[0]);
            var current = self.querySelector(pair[0]);
            if (next && current) current.innerHTML = next.innerHTML;
          });
          openIds.forEach(function (id) {
            var d = document.getElementById(id);
            if (d) d.setAttribute('open', '');
          });
          if (focusedId) {
            var f = document.getElementById(focusedId);
            if (f) f.focus({ preventScroll: true });
          }
          if (push) window.history.pushState({ facets: true }, '', url);
          if (scroll) {
            var content = self.querySelector('[data-facets-content]');
            if (content && content.getBoundingClientRect().top < 0) content.scrollIntoView({ behavior: 'smooth' });
          }
        })
        .catch(function () {
          window.location.href = url;
        })
        .finally(function () {
          var r = self.querySelector('[data-results]');
          if (r) r.classList.remove('is-loading');
        });
    }
  }
  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);

  /* ------------------------------------------------------- recommendations */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.getAttribute('data-url')) return;
      var self = this;
      var load = function () {
        fetch(self.getAttribute('data-url'))
          .then(function (r) {
            return r.text();
          })
          .then(function (html) {
            var rec = parseHTML(html).querySelector('product-recommendations');
            if (rec && rec.innerHTML.trim().length) self.innerHTML = rec.innerHTML;
            else self.hidden = true;
          })
          .catch(function () {
            self.hidden = true;
          });
      };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting) return;
            io.disconnect();
            load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        io.observe(this);
      } else {
        load();
      }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ----------------------------------------------------------------- share */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      var self = this;
      var button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', function () {
        var url = self.getAttribute('data-url') || window.location.href;
        if (navigator.share && window.matchMedia('(hover: none)').matches) {
          navigator.share({ title: self.getAttribute('data-title') || document.title, url: url }).catch(function () {});
          return;
        }
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(url).then(function () {
          var label = self.querySelector('[data-share-label]');
          if (!label) return;
          var previous = label.textContent;
          label.textContent = strings.linkCopied;
          setTimeout(function () {
            label.textContent = previous;
          }, 2000);
        });
      });
    }
  }
  if (!customElements.get('share-button')) customElements.define('share-button', ShareButton);

  /* ------------------------------------------------------------- reveal-in */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  function init(root) {
    initDropdowns(root);
    initReveal(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  theme.formatMoney = formatMoney;
  theme.toast = toast;
  theme.openDrawer = openDrawer;
  theme.closeDrawer = closeDrawer;
  window.theme = theme;
})();
